// src/api/utils/errorMapper.ts
/**
 * Maps an AxiosError (or generic Error) to a normalized ApiError shape.
 * The backend returns errors in the shape of AuthErrorResponseDTO or similar
 * structures. This function extracts the most useful information while
 * protecting against missing fields.
 *
 * Security considerations:
 * - Only expose error messages that originate from the backend DTOs or the
 *   HTTP status text. For unknown errors we fall back to a generic message to
 *   avoid leaking internal details.
 * - The function never returns raw stack traces or other potentially sensitive
 *   information.
 */
import { AxiosError } from 'axios';
import { ApiError } from '../types';

/**
 * Normalises an error coming from Axios (or a generic Error) into an {@link ApiError}.
 *
 * @param error - The error thrown by Axios. It may be a plain Error without a response.
 * @returns An {@link ApiError} with a stable shape suitable for UI consumption.
 */
export function mapErrorResponse(error: AxiosError): ApiError {
  // Extract the HTTP status if present – it may be undefined for network errors.
  const status = error.response?.status;

  // Default fallback for unknown or network errors.
  const fallback: ApiError = {
    code: 'UNKNOWN_ERROR',
    // Use the original error message only for network errors; otherwise we rely on
    // backend‑provided messages to avoid leaking implementation details.
    message: error.message || 'An unknown error occurred',
    // Include status only when we have a concrete HTTP status code.
    ...(status !== undefined ? { status } : {}),
  };

  // If there is no response object we are dealing with a network error or a
  // cancelled request – return the generic fallback.
  if (!error.response) {
    return fallback;
  }

  const { data } = error.response;

  // If the backend follows the AuthErrorResponseDTO shape, map it.
  if (data && typeof data === 'object' && 'errorCode' in data && 'message' in data) {
    const { errorCode, message } = data as { errorCode: string; message: string };
    return {
      code: errorCode,
      message,
      ...(status !== undefined ? { status } : {}),
    };
  }

  // Otherwise, try to use generic fields that some endpoints may return.
  if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
    const { code, message } = data as { code: string; message: string };
    return { code, message, ...(status !== undefined ? { status } : {}) };
  }

  // Spring/Tomcat may return plain text for failures that occur before a
  // controller is reached, such as CORS rejection. Surface that text rather
  // than the unhelpful Axios "Request failed with status code ..." message.
  if (typeof data === 'string' && data.trim().length > 0) {
    const message = data.includes('Invalid CORS request')
      ? 'The server rejected this browser request because its Origin is not allowed. Check the backend CORS deployment settings.'
      : data.trim();
    return {
      code: status !== undefined ? `HTTP_${status}` : 'SERVER_ERROR',
      message,
      ...(status !== undefined ? { status } : {}),
    };
  }

  // Fallback to status text if available. If the status is missing we revert to the
  // generic UNKNOWN_ERROR code to keep the shape consistent.
  if (status !== undefined) {
    return {
      code: `HTTP_${status}`,
      message: error.response.statusText || fallback.message,
      status,
    };
  }

  // As a last resort, return the generic fallback.
  return fallback;
}
